import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

const ArrivedStatusReport = () => {
  const [reportList, setReportList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [consigneeId, setConsigneeId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reportType, setReportType] = useState("full");

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        report_type: reportType,
      };

      if (reportType === "party" && consigneeId) {
        payload.consignee_id = consigneeId;
      }

      if (fromDate) {
        payload.from_date = fromDate;
      }

      if (toDate) {
        payload.to_date = toDate;
      }

      const response = await post("/reports/party_wise_arrived_status_report/list", payload);
      if (response.status === 200) {
        setReportList(response.data || []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [showAlert, consigneeId, fromDate, toDate]);

  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
        setConsigneeOptions([
          { value: "", label: "Select Consignee" },
          ...response.data.map(item => ({
            value: item.consignees_id,
            label: item.consignees_name,
          }))
        ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchConsigneeData();
  }, [fetchData, fetchConsigneeData]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", accessorKey: "si_no", enableColumnFilter: false, enableSorting: false },
      { header: "Job No", accessorKey: "job_no" },
      { header: "Consignee", accessorKey: "consignee" },
      { header: "Shipper", accessorKey: "shipper" },
      { header: "Desc", accessorKey: "description" },
      { header: "POL", accessorKey: "pol" },
      { header: "Liner", accessorKey: "liner" },
      { header: "BL No", accessorKey: "bl_number" },
      { header: "TC", accessorKey: "no_of_containers", isTotal: true },
      { header: "20'", accessorKey: "count_20ft" },
      { header: "40'", accessorKey: "count_40ft" },
      { header: "LCL", accessorKey: "count_lcl" },
      { header: "Arrived", accessorKey: "arrived" },
      { header: "STR FD", accessorKey: "free_days" },
      { header: "DET FREE DATE", accessorKey: "dem_upto" },
      { header: "VSL / VOY", accessorKey: "vessel_detail" },
      { header: "Weight", accessorKey: "weight" , isTotal: true},
      { header: "BE No", accessorKey: "be_number" },
      { header: "BE Date", accessorKey: "be_date" },
      { header: "Status", accessorKey: "status", label_condition: "job_status" },
    ],
    []
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Party Wise - Arrived Status Report</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Report Type</label>
              <select
                className="form-select"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="full">Full</option>
                <option value="party">Party Wise</option>
              </select>
            </div>
            {reportType === "party" && (
              <div className="col-md-3">
                <label className="form-label">Consignee</label>
                <select
                  className="form-select"
                  value={consigneeId}
                  onChange={(e) => setConsigneeId(e.target.value)}
                >
                  {consigneeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label">From Date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">To Date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={fetchData} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Party Wise - Arrived Status Report</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Party Wise Arrived Status Report"
            columns={columns}
            data={reportList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>
    </>
  );
};

export default ArrivedStatusReport;
